import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import useStructuredData from "../hooks/useStructuredData";
import { getBreadcrumbTrail } from "../utils/breadcrumbs";

interface BreadcrumbsProps {
  /** Real content for the one nested route this app has (/success-stories/case-studies/:slug) — see breadcrumbs.ts. */
  dynamicLabel?: string;
}

/** Renders nothing on the homepage (a single "Home" crumb isn't a trail). */
export default function Breadcrumbs({ dynamicLabel }: BreadcrumbsProps) {
  const location = useLocation();
  const trail = getBreadcrumbTrail(location.pathname, dynamicLabel);

  // Memoized on the trail's own content (not just re-created every render) —
  // useStructuredData's effect keys off referential identity, and this
  // component has no memo boundary of its own, so an unmemoized object
  // literal here would remove/re-append the JSON-LD <script> on every
  // unrelated re-render of whichever page mounts it (the same dormant bug
  // class ZP-68 fixed with useMemo on Home.tsx/LegalRegistrations.tsx).
  const schema = useMemo(
    () =>
      trail.length > 1
        ? {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: trail.map((crumb, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: crumb.label,
              item: `${window.location.origin}${crumb.path}`,
            })),
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trail is a fresh array each render; its serialized content is the real dependency.
    [JSON.stringify(trail)]
  );

  useStructuredData(schema);

  if (trail.length <= 1) return null;

  return (
    <nav aria-label="Breadcrumb" className="mx-auto max-w-6xl px-6 pt-6">
      <ol className="flex items-center gap-1.5 text-xs text-ocean-800/50 dark:text-foam-100/50">
        {trail.map((crumb, i) => {
          const isLast = i === trail.length - 1;
          // Mobile: only the immediate parent + current page, per this
          // ticket's own "truncate, don't wrap" AC — earlier crumbs (Home,
          // on a 3-level trail) are hidden below sm: rather than wrapped.
          const hiddenOnMobile = trail.length > 2 && i < trail.length - 2;
          return (
            <li key={crumb.path} className={`flex items-center gap-1.5 ${hiddenOnMobile ? "hidden sm:flex" : "flex"}`}>
              {i > 0 && <ChevronRight className="h-3 w-3 shrink-0" />}
              {isLast ? (
                <span aria-current="page" className="font-medium text-ocean-800 dark:text-foam-100">
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.path} className="hover:text-teal-600">
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
